import { CreateKnowledgeBaseSnippetPayload, KnowledgeBaseClient } from 'api/clients/KnowledgeBaseClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { groupBy } from 'lodash'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { KnowledgeBaseSnippet, KnowledgeBaseSnippetKind } from 'types'
import { reportError } from 'utils/error'
import { SnippetKindLayout } from 'screens/BrandKits/utils'
import { buildErrorMessage } from 'utils'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import store from 'redux/store'
import { useCurrentWorkspace } from './useCurrentWorkspace'
import { workspacesSet } from 'redux/actions'
import { useWorkspaces } from './useWorkspaces'

export const groupSnippetsByKind = (snippets: KnowledgeBaseSnippet[]) => {
  const snippetsByKind = groupBy(snippets, 'kind')

  return Object.keys(snippetsByKind).map((kind) => ({
    name: SnippetKindLayout[kind as KnowledgeBaseSnippetKind].title,
    children: snippetsByKind[kind],
  }))
}

const allSnippetsQueryKey = (workspaceId: number) => [workspaceId, 'snippets']

export const useSnippets = () => {
  const workspaceId = useCurrentWorkspaceId()

  return useQuery({
    queryKey: allSnippetsQueryKey(workspaceId),
    queryFn: async () => {
      const res = await KnowledgeBaseClient.getSnippets()
      if (res.type === SUCCESS_RESPONSE_TYPE)
        return res.data.knowledge_base_snippets.sort((a, b) => a.use_count - b.use_count)
      throw new Error('Error fetching snippets')
    },
    onError: reportError,
    refetchOnWindowFocus: false,
  })
}

export const useUpdateSnippet = () => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  return useMutation(
    async (snippet: KnowledgeBaseSnippet) => {
      const response = await KnowledgeBaseClient.updateSnippet(snippet)

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        reportError('Failed to create snippet', {
          metaData: {
            snippet,
            response: response.data,
          },
        })
        if ('status' in response && response.status === 422) {
          throw new Error('Name has already been taken')
        } else {
          showToastMessage('We found an error updating the snippet. Our team has been notified', 'error')
          throw new Error(buildErrorMessage(response.data.errors) || 'Error updating snippet')
        }
      }
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(allSnippetsQueryKey(workspaceId))
        showToastMessage('Snippet updated')
      },
    },
  )
}

export const useUpdateSnippetUsage = () => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  return useMutation(
    async (snippet: KnowledgeBaseSnippet) => {
      const res = await KnowledgeBaseClient.incrementUse(snippet)
      if (res.type !== SUCCESS_RESPONSE_TYPE) throw new Error('Error updating snippet')
      return res.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(allSnippetsQueryKey(workspaceId))
      },
    },
  )
}

export const useDeleteSnippet = (opts?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  return useMutation(
    async (snippet: KnowledgeBaseSnippet) => {
      const res = await KnowledgeBaseClient.deleteSnippet(snippet)
      if (res.type !== SUCCESS_RESPONSE_TYPE) throw new Error('Error deleting snippet')
      return res.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(allSnippetsQueryKey(workspaceId))
        showToastMessage('Snippet deleted')
        opts?.onSuccess?.()
      },
      onError: (error) => {
        reportError(error)
        showToastMessage('We found an error deleting the snippet. Our team has been notified', 'error')
      },
    },
  )
}

export const useCreateSnippet = () => {
  const queryClient = useQueryClient()
  const workspace = useCurrentWorkspace()
  const workspaces = useWorkspaces()
  const workspaceId = useCurrentWorkspaceId()

  return useMutation(
    async (snippet: CreateKnowledgeBaseSnippetPayload) => {
      const response = await KnowledgeBaseClient.createSnippet(snippet)

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        reportError('Failed to create snippet', {
          metaData: {
            snippet,
            response: response.data,
          },
        })

        if ('status' in response && response.status === 422) {
          throw new Error('Name has already been taken')
        } else {
          showToastMessage('We found an error creating the snippet. Our team has been notified', 'error')
          throw new Error(buildErrorMessage(response.data.errors) || 'Error creating snippet')
        }
      }

      if (workspace && !workspace.snippets_enabled) {
        const res = await WorkspaceClient.update(workspaceId, true)
        if (res.type === SUCCESS_RESPONSE_TYPE) {
          store.dispatch(
            workspacesSet(workspaces.map((ws) => (ws.id === workspaceId ? { ...ws, snippets_enabled: true } : ws))),
          )
        }
      }

      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(allSnippetsQueryKey(workspaceId))
        showToastMessage('Snippet created')
      },
    },
  )
}
